/**
 * @file URL query bootstrap: template, locale, prefill, `importId` (CRM), submit target, extra POST payload.
 * @module urlParams
 *
 * - `templateId` — locks template select when present.
 * - `locale` — `en` or `es`; locks language toggle when present.
 * - `prefill` / `prefillB64` — JSON keyed by field id.
 * - `fields` — JSON flat fields applied before prefill (prefill wins on keys).
 * - `firstName` — shorthand merged last on top of prefill.
 * - `importId` — CRM id; SPA loads JSON from `GET https://crm.wetag.ar/api/talenthub/import/:id` (CORS).
 * - `additionalData` — JSON forwarded with submit body when `submitUrl` is set.
 * - `submitUrl` — `http`/`https`; switches primary action to POST instead of client PDF.
 */

/**
 * URL-safe Base64 decode for `prefillB64`.
 * @param {string} b64url
 * @returns {string}
 */
function base64UrlDecode(b64url) {
	const pad = b64url.length % 4 === 0 ? "" : "=".repeat(4 - (b64url.length % 4));
	const b64 = b64url.replace(/-/g, "+").replace(/_/g, "/") + pad;
	return atob(b64);
}

/**
 * @param {string} urlStr
 * @returns {boolean}
 */
export function isAllowedSubmitEndpoint(urlStr) {
	if (!urlStr || typeof urlStr !== "string") return false;
	try {
		const u = new URL(urlStr, window.location.href);
		return u.protocol === "https:" || u.protocol === "http:";
	} catch {
		return false;
	}
}

/**
 * Mezcla `prefill` sobre `target` (objeto plano del formulario; listas reemplazan por array).
 * @param {Record<string, unknown>} target
 * @param {Record<string, unknown>} prefill
 * @returns {Record<string, unknown>}
 */
export function mergePrefillInto(target, prefill) {
	if (!prefill || typeof prefill !== "object" || Array.isArray(prefill)) {
		return target;
	}
	const out = { ...target };
	for (const [k, v] of Object.entries(prefill)) {
		if (Array.isArray(v)) {
			out[k] = v.map((item) =>
				item && typeof item === "object" && !Array.isArray(item)
					? { ...item }
					: item
			);
		} else if (v && typeof v === "object" && !Array.isArray(v)) {
			const prev = out[k];
			out[k] = mergePrefillInto(
				typeof prev === "object" && prev && !Array.isArray(prev)
					? /** @type {Record<string, unknown>} */ (prev)
					: {},
        /** @type {Record<string, unknown>} */(v)
			);
		} else {
			out[k] = v;
		}
	}
	return out;
}

/**
 * @param {URLSearchParams} params
 * @param {string} key
 * @returns {unknown} `null` si falta o no es JSON válido.
 */
function tryParseJsonParam(params, key) {
	const raw = params.get(key);
	if (!raw) return null;
	try {
		return JSON.parse(decodeURIComponent(raw.replace(/\+/g, " ")));
	} catch {
		try {
			return JSON.parse(raw);
		} catch {
			return null;
		}
	}
}

/**
 * @param {ReadonlyArray<{ id: string }>} templates Registro de plantillas (p. ej. `CV_TEMPLATES`).
 * @returns {{
 *   templateId: string | null,
 *   locale: "en" | "es" | null,
 *   lockTemplate: boolean,
 *   lockLocale: boolean,
 *   prefill: Record<string, unknown> | null,
 *   additionalData: unknown,
 *   submitUrl: string,
 *   hasSubmitUrl: boolean,
 *   importId: string | null,
 * }}
 */
export function parseCvUrlParams(templates) {
	const params = new URLSearchParams(window.location.search);

	const templateRaw = (params.get("templateId") || "").trim();
	const templateId =
		templateRaw &&
			templates.some((t) => t.id === templateRaw)
			? templateRaw
			: null;

	const localeRaw = (params.get("locale") || "").toLowerCase().trim();
	const locale =
		localeRaw === "en" || localeRaw === "es"
			? /** @type {"en" | "es"} */ (localeRaw)
			: null;

	/** @type {Record<string, unknown> | null} */
	let prefill = null;

	const b64 = params.get("prefillB64");
	if (b64) {
		try {
			prefill = /** @type {Record<string, unknown>} */ (
				JSON.parse(base64UrlDecode(b64.trim()))
			);
		} catch {
			/* JSON inválido */
		}
	}
	if (!prefill) {
		const p = tryParseJsonParam(params, "prefill");
		if (p && typeof p === "object" && !Array.isArray(p)) {
			prefill = /** @type {Record<string, unknown>} */ (p);
		}
	}

	const fieldsParam = tryParseJsonParam(params, "fields");
	if (fieldsParam && typeof fieldsParam === "object" && !Array.isArray(fieldsParam)) {
		const f = /** @type {Record<string, unknown>} */ (fieldsParam);
		prefill = prefill ? mergePrefillInto(f, prefill) : f;
	}

	const firstNameQ = (
		params.get("firstName") ||
		params.get("firstname") ||
		""
	).trim();
	if (firstNameQ) {
		prefill = mergePrefillInto(prefill ?? {}, { firstName: firstNameQ });
	}

	const additionalData = tryParseJsonParam(params, "additionalData");

	const submitUrlRaw = (params.get("submitUrl") || "").trim();
	const submitAllowed = isAllowedSubmitEndpoint(submitUrlRaw);
	const submitUrl = submitAllowed ? submitUrlRaw : "";

	const importIdRaw = (params.get("importId") || "").trim();
	const importId = importIdRaw.length > 0 ? importIdRaw : null;

	return {
		templateId,
		locale,
		lockTemplate: Boolean(templateId),
		lockLocale: Boolean(locale),
		prefill,
		additionalData: additionalData ?? null,
		submitUrl,
		hasSubmitUrl: Boolean(submitUrl),
		importId,
	};
}
